import { Component, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { VDialogRef } from "@app/directives/_layout/v-dialog/v-dialog-ref";
import { DialogService } from "@app/directives/_layout/v-dialog/v-dialog.service";
import { VTabComponent } from "@app/directives/_layout/v-tabs/v-tab.component";
import { VTabsComponent } from "@app/directives/_layout/v-tabs/v-tabs.component";
import { UserType } from "@app/enum/UserType";
import { SavedState } from "@app/interfaces/IPeripheral";
import { IUser } from "@app/interfaces/IUser";
import { CameraChannelService } from "@app/main/modules/cameras/_services/camera-channels.service";
import { FoundationNavigationModule } from "@app/main/modules/foundation/foundation.navigation";
import { EngineerCommissionService } from "@app/main/modules/foundation/_services/engineer-commission.service";
import { EngineerJobItemService } from "@app/main/modules/foundation/_services/engineer-job-item.service";
import { VehicleValidationCheckRecordService } from "@app/main/modules/foundation/_services/vehicle-validation-check-record.service";
import { FoundationVehiclesService } from "@app/main/modules/foundation/_services/vehicle.service";
import { CurrentUserService } from "@app/services/currentUser.service";
import { NavigationService } from "@app/services/navigation.service";
import { TranslationService } from "@app/services/translation.service";
import copy from "fast-copy";
import { forkJoin, of } from "rxjs";
import { switchMap, take } from "rxjs/operators";
import { EngineerCommissionStatus, EngineerJobItemType } from "../../../foundation.enums";
import { IEngineerCommission, IProcessResult } from "../../../foundation.interfaces";
import './commission-editor.component.scss';
import { DeviceInstallEditor } from "./components/deviceInstallEditor/device-install-editor.component";


@Component({
    selector: 'commission-editor',
    template: require('./commission-editor.component.html')
})
export class VCommissionEditorComponent implements OnInit {
    jobItemId: number;
    customerId: number;
    commission: IEngineerCommission;

    loading: boolean;
    savingCommission: boolean;
    currentUser: IUser;

    onFinishRedirectTo = "app.foundation.commissioning";

    EngineerJobItemType = EngineerJobItemType;

    dialog: VDialogRef;

    errorTitle: string;
    errorMessage: string;
    mappingResult: IProcessResult;

    @ViewChild(DeviceInstallEditor) deviceInstallEditor: DeviceInstallEditor;
    @ViewChild('confirmSaveTemplate') confirmSaveTemplate: TemplateRef<any>;
    @ViewChild('errorTemplate') errorTemplate: TemplateRef<any>;
    @ViewChild('tabs') tabsComponent: VTabsComponent;
    @ViewChild('tab1') tab1: VTabComponent;
    @ViewChild('tab2') tab2: VTabComponent;
    @ViewChild('tab3') tab3: VTabComponent;


    constructor(
        private readonly currentUserService: CurrentUserService,
        private readonly commissionService: EngineerCommissionService,
        private readonly dialogService: DialogService,
        private readonly navigationService: NavigationService,
        private readonly foundationVehicleService: FoundationVehiclesService,
        private readonly engineerJobItemService: EngineerJobItemService,
        private readonly translationService: TranslationService,
        private readonly cameraChannelService: CameraChannelService,
        private readonly vehicleValidationCheckRecordService: VehicleValidationCheckRecordService
    ) { }

    ngOnInit(): void {
        if (!isNaN(this.navigationService.getRouteParam('jobItemId'))) {
            this.jobItemId = this.navigationService.getRouteParam('jobItemId');
        }

        if (!isNaN(this.navigationService.getRouteParam('customerId'))) {
            this.customerId = this.navigationService.getRouteParam('customerId');
        }

        this.currentUserService.getCurrentUser().then((user) => {
            this.currentUser = user;
            this.loadData();
        })
    }

    confirmSave(): void {
        this.dialog = this.dialogService.open(this.confirmSaveTemplate, null);
    }

    confirmSaveAndCloseDialog(): void {
        this.closeDialog();
        this.saveCommission();
    }

    cancelSave(): void {
        this.closeDialog();
        this.tab3.disablePrev = false;
        this.tabsComponent.selectTab(this.tab2);
    }

    closeDialog(): void {
        this.dialog.close();
    }

    confirmErrorAndCloseDialog(): void {
        this.closeDialog();
        this.rerouteAfterError();
    }

    onCommissionChanged(commission: IEngineerCommission): void {
        this.commission = copy(commission);
    }

    onSelectedTabChanged(tab: VTabComponent): void {
        if (tab.tabIndex == 0 && !!this.commission) {
            this.commission.Serial = null;
            this.commission.UnitLocation = null;
            this.commission.existingHardwareAcquired = false;
        }
    }

    //--

    private saveCommission(): void {
        this.savingCommission = true;

        if (!this.commission.Serial) {
            this.commission.vehicle.UnitLocation = null;
        }

        this.addMissingCommissionProperties(this.commission);

        (this.commissionService.processCommissionMappings(this.commission)).pipe(take(1)).subscribe(mappingResult => {

            if (!mappingResult.IsSuccess) {
                this.errorTitle = this.translationService.t("CommissionEditor.ProcessErrorTitle");
                this.errorMessage = this.translationService.t("CommissionEditor.ProcessErrorMessage", mappingResult.ExceptionMessage);
                this.dialog = this.dialogService.open(this.errorTemplate, null);
                return;
            }

            (this.commissionService.createUpdateEngineerCommission(this.commission).pipe(take(1)).subscribe(commissionResult => {
                this.mappingResult = commissionResult;

                if (!commissionResult.IsSuccess) {
                    this.errorTitle = this.translationService.t("CommissionEditor.CommissionErrorTitle");
                    this.errorMessage = this.translationService.t("CommissionEditor.CommissionErrorMessage", mappingResult.ExceptionMessage);
                    this.dialog = this.dialogService.open(this.errorTemplate, null);
                    return;
                }

                this.commission.Id = commissionResult.Value.Id

                const pendingCamera = this.commission.pendingCommissionChanges?.find(device => device.isCamera && device.Unit.savedState === SavedState.AddedRequiresSave);
                const pendingCameraId = this.commission.pendingCommissionChanges?.find(device => device.isCamera)?.Unit?.Serial;

                forkJoin({
                    completeJobItem: !!this.jobItemId ? this.engineerJobItemService.completeEngineerJobItem(this.jobItemId) : of(null),
                    updateChannels: !!pendingCamera ? this.cameraChannelService.getCommissionChannels(pendingCameraId, this.commission.VehicleId).pipe(
                        switchMap((channels) => {
                            return this.cameraChannelService.saveCommissionChannels(channels).pipe(take(1));
                        })
                    ) : of(null),
                    updateVehicle: this.foundationVehicleService.createUpdateVehicle(this.commission.vehicle),
                    associateCommissionIdToCheckRecords: this.vehicleValidationCheckRecordService.associateVehicleValidationCheckRecordsToCommission(this.commission.deviceValidationCheckRecordIds, this.commission.Id)
                }).pipe(
                    take(1)
                ).subscribe(() => {
                    this.savingCommission = false;
                });
            }));
        })
    }

    private loadData(): void {
        this.commission = {
            Id: 0,
            Status: EngineerCommissionStatus.Submitted,
            JobItemId: this.jobItemId,
            AccountId: this.customerId,
            pendingCommissionChanges: [],
            VisitReasonId: EngineerJobItemType.Install,
            UserId: this.currentUser.Id,
            Comments: "",
            EngineerId: 57,
            Metadata: {},
            deviceValidationCheckRecordIds: [],
            existingHardwareAcquired: false
        }

    }

    private addMissingCommissionProperties(commission: IEngineerCommission): void {
        if (!commission) {
            return;
        }
        commission.Client = commission.Client ?? "";
        commission.Make = commission.Make ?? "";
        commission.Model = commission.Model ?? "";
        commission.ShortReg = commission.vehicle.ShortReg ?? ""
        commission.Serial = commission.Serial ?? "";
        commission.CanbusId = commission.CanbusId ?? "";
        commission.CameraId = commission.CameraId ?? "";
        commission.FmiId = commission.FmiId ?? "";
        commission.DigidlId = commission.DigidlId ?? "";
        commission.MessagingId = commission.MessagingId ?? "";
        commission.TdtId = commission.TdtId ?? "";
        commission.PtoInputState = commission.PtoInputState ?? "";
        commission.PtoDescription = commission.PtoDescription ?? "";
        commission.Pto = commission.Pto ?? false;

        commission.Comments = this.concatenateCommentsSections(commission);
        commission.UnitLocation = commission.UnitLocation ?? "";
        commission.Accelerometer = commission.Accelerometer ?? false;

        commission.VisitReason = EngineerJobItemType[commission.VisitReasonId];
    }

    private concatenateCommentsSections(commission: IEngineerCommission): string {

        let concatenatedComments = "";
        if (commission.Metadata?.NoFaultFound) {
            let comment = this.translationService.t("CommissionEditor.NoChanges");
            concatenatedComments += `***${comment}*** :: `;
        }

        if (commission.Comments?.length > 0) {
            concatenatedComments += `${commission.Comments}`;
        }

        if (commission.Chargeable && commission.ChargeReason?.length > 0) {
            let comment = this.translationService.t("CommissionEditor.ChargeableComments");
            concatenatedComments += ` :: ${comment}: ${commission.ChargeReason}`;
        }

        commission.pendingCommissionChanges.forEach(device => {
            if (device.faultFlagged) {
                let comment = this.translationService.t("CommissionEditor.EngineerFault")
                concatenatedComments += ` :: ***${comment}: ${device.Unit.Serial} - ${device.faultComments}***`;
            }
        });

        return concatenatedComments;
    }

    private rerouteAfterError(): void {
        // Should this go back to outside the commission?
        // Engineers back to jobItem main page?
        // Internal users back to commission main page?
        if (this.currentUser.Type === UserType.Engineer) {
            this.navigationService.go(FoundationNavigationModule.JobItems, null, true);
        } else if (!!this.commission.JobItemId) {
            this.navigationService.go(FoundationNavigationModule.Jobs);
        } else {
            this.navigationService.go(FoundationNavigationModule.Commissioning, null, true);
        }
    }
}
